import type { ClientAnalyticsEvent } from "@townsy/contracts";
import { postAnalyticsBatch } from "../api/endpoints";
import { clientEventId } from "../lib/env";
import { STORAGE_KEYS, readJson, writeJson } from "../lib/storage";
import type { SignVisibilitySample } from "../scene/types";
import { PANEL_DEDUPE_WINDOW_MS, SIGN_DEDUPE_WINDOW_MS, WindowDedupe } from "./dedupe";
import { AnalyticsQueue } from "./queue";
import { SignTracker } from "./signTracker";

/**
 * A sign smaller than this on screen (CSS px, smaller projected dimension) is not readable and
 * never becomes an impression candidate, whatever its visible fraction. The 1366x768 overview
 * shows rooftop signs at 64 px and more (docs/01 section 5); this is a floor, not that target.
 */
export const SIGN_MIN_PROJECTED_PX = 24;

/**
 * Measurement candidates from the browser (docs/07 sections 5-6). They are candidates only:
 * the API validates, rate-limits and dedupes them, and nothing here can award anything.
 */
export interface Analytics {
  trackPanelView(propertyId: string): void;
  ingestSignSamples(samples: readonly SignVisibilitySample[]): void;
  /** Forget in-progress sign exposures (room change, scene disposed). */
  resetSigns(): void;
  start(): void;
  stop(): void;
}

/**
 * Only advertising surfaces are measured. Townsy-run civic buildings (the Real Estate office) have
 * no owner and no placement, so their signs and panels never become statistics candidates.
 */
export function measurableSamples(
  samples: readonly SignVisibilitySample[],
  isMeasurable: (propertyId: string) => boolean,
): SignVisibilitySample[] {
  return samples.filter((s) => isMeasurable(s.propertyId));
}

export function createAnalytics(opts: { now?: () => number; isMeasurable?: (propertyId: string) => boolean } = {}): Analytics {
  const now = opts.now ?? (() => Date.now());
  const isMeasurable = opts.isMeasurable ?? (() => true);
  const queue = new AnalyticsQueue({
    post: (events, o) => postAnalyticsBatch(events, o),
    onError: (err) => console.warn("[analytics] batch failed", err),
  });
  const signDedupe = new WindowDedupe(SIGN_DEDUPE_WINDOW_MS, {
    load: () => readJson<Record<string, number>>("session", STORAGE_KEYS.signDedupe),
    save: (m) => writeJson("session", STORAGE_KEYS.signDedupe, m),
  });
  const panelDedupe = new WindowDedupe(PANEL_DEDUPE_WINDOW_MS);
  const tracker = new SignTracker({ dedupe: signDedupe, minProjectedPx: SIGN_MIN_PROJECTED_PX });
  let started = false;

  const push = (event: ClientAnalyticsEvent) => queue.push(event);

  const onVisibility = () => {
    if (document.visibilityState !== "visible") {
      tracker.reset();
      void queue.flush({ keepalive: true });
    }
  };
  const onPageHide = () => {
    void queue.flush({ keepalive: true });
  };

  return {
    trackPanelView(propertyId) {
      if (!isMeasurable(propertyId)) return;
      if (!panelDedupe.allow(propertyId, now())) return;
      push({ type: "panel_view", propertyId, clientEventId: clientEventId() });
    },
    ingestSignSamples(samples) {
      const visible = typeof document === "undefined" ? true : document.visibilityState === "visible";
      for (const c of tracker.ingest(measurableSamples(samples, isMeasurable), now(), visible)) {
        push({
          type: "sign_impression",
          propertyId: c.propertyId,
          clientEventId: clientEventId(),
          visibleFraction: c.visibleFraction,
          continuousMs: c.continuousMs,
          signKind: c.signKind,
        });
      }
    },
    resetSigns() {
      tracker.reset();
    },
    start() {
      if (started || typeof document === "undefined") return;
      started = true;
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", onPageHide);
      queue.start();
    },
    stop() {
      if (!started) return;
      started = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      void queue.flush({ keepalive: true });
      queue.stop();
      tracker.reset();
    },
  };
}
